import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  SwapVert as SortIcon,
  Check as CheckIcon,
  MenuBook as ShelfIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { bibliotequesAPI, rayonnagesAPI } from '../services/api';

function BibliothequeCard({ bibliotheque, rayonnages = [] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleViewBooks = (bibliothequeId) => {
    navigate(`/livres?bibliotheque_id=${bibliothequeId}`);
  };

  const handleViewRayonnageBooks = (rayonnageId) => {
    navigate(`/livres?rayonnage_id=${rayonnageId}`);
  };

  const handleViewDetails = (bibliothequeId) => {
    navigate(`/bibliotheques/${bibliothequeId}`);
  };

  return (
    <Box
      onClick={() => handleViewDetails(bibliotheque.id)}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        px: 3,
        py: 1.25,
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        '&:last-of-type': {
          borderBottom: 'none',
        },
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      {/* Colonne gauche : nom, description, rayonnages */}
      <Box sx={{ flexGrow: 1, minWidth: 0, pr: 2 }}>
        {/* Nom */}
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontWeight: 700,
            fontSize: '16px',
            mt: 2,
            mb: 1,
            lineHeight: 1.2,
            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
          }}
        >
          {bibliotheque.nom}
        </Typography>

        {/* Description */}
        {bibliotheque.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              mt: 0,
              m: 0,
              lineHeight: 1.3,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {bibliotheque.description}
          </Typography>
        )}

        {/* Rayonnages */}
        {rayonnages && rayonnages.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5, mb: 2 }}>
            {rayonnages.map((rayonnage) => (
              <Chip
                key={rayonnage.id}
                label={`${rayonnage.nom} (${rayonnage.nombre_livres || 0})`}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewRayonnageBooks(rayonnage.id);
                }}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: 'grey.200',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'grey.300',
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Colonne droite : nombre de livres */}
      <Box 
        onClick={(e) => {
          e.stopPropagation();
          handleViewBooks(bibliotheque.id);
        }}
        sx={{ 
          textAlign: 'right', 
          px: 2,
          py: 1,
          borderRadius: 2,
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.15)',
            transform: 'scale(1.05)',
          },
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: 400,
            color: 'primary.main',
            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
          }}
        >
          {bibliotheque.nombre_livres || 0}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'primary.main',
            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
          }}
        >
          {bibliotheque.nombre_livres === 1 
            ? t('bibliotequesPage.bookCount', { count: bibliotheque.nombre_livres || 0 })
            : t('bibliotequesPage.bookCountPlural', { count: bibliotheque.nombre_livres || 0 })}
        </Typography>
      </Box>
    </Box>
  );
}

function BibliotequesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [sortType, setSortType] = useState('nom-asc');
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);

  // Requête pour récupérer les bibliothèques
  const { data, isLoading, error } = useQuery(
    ['bibliotheques', search, sortType],
    () => bibliotequesAPI.getAll({ search, sort: sortType }).then(res => res.data.data),
    {
      staleTime: 2 * 60 * 1000,
    }
  );

  // Récupérer les rayonnages pour chaque bibliothèque
  const bibliothequeIds = data?.map(b => b.id) || [];
  const { data: rayonnagesData } = useQuery(
    ['rayonnages-by-bibliotheque', bibliothequeIds],
    async () => {
      const rayonnagesMap = {};
      for (const id of bibliothequeIds) {
        try {
          const res = await rayonnagesAPI.getByBibliotheque(id);
          rayonnagesMap[id] = res.data.data || [];
        } catch (err) {
          rayonnagesMap[id] = [];
        }
      }
      return rayonnagesMap;
    },
    {
      enabled: bibliothequeIds.length > 0,
      staleTime: 2 * 60 * 1000,
    }
  );

  const handleAdd = () => {
    navigate('/bibliotheques/ajouter');
  };


  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleSortMenuOpen = (event) => {
    setSortMenuAnchor(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };

  const handleSortSelect = (sortValue) => {
    setSortType(sortValue);
    handleSortMenuClose();
  };

  const getSortLabel = () => {
    switch (sortType) {
      case 'nom-asc':
        return t('bibliotequesPage.sort.nameAsc');
      case 'nom-desc':
        return t('bibliotequesPage.sort.nameDesc');
      case 'livres-desc':
        return t('bibliotequesPage.sort.booksDesc');
      case 'livres-asc':
        return t('bibliotequesPage.sort.booksAsc');
      default:
        return t('bibliotequesPage.sort.nameAsc');
    }
  };

  if (error) {
    return (
      <Alert severity="error">
        {t('bibliotequesPage.loadingError', { message: error.message })}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Barre de recherche et boutons */}
      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, mb: 3, alignItems: 'center', flexWrap: { xs: 'nowrap', sm: 'nowrap' } }}>
        <TextField
          fullWidth
          placeholder={t('bibliotequesPage.searchPlaceholder')}
          value={search}
          onChange={handleSearchChange}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.default',
              borderRadius: 2,
            }
          }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          }}
        />
        <Button
          variant="outlined"
          startIcon={<SortIcon />}
          onClick={handleSortMenuOpen}
          sx={{
            height: '52px',
            borderRadius: 2,
            fontSize: { xs: '14px', sm: '14px' },
            whiteSpace: 'nowrap',
            backgroundColor: 'background.default',
            px: { xs: 1, sm: 1.5 },
            flexShrink: 0,
            '& .MuiButton-startIcon': {
              mr: { xs: 0, sm: 0.75 }
            }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {getSortLabel()}
          </Box>
        </Button>
        <Menu
          anchorEl={sortMenuAnchor}
          open={Boolean(sortMenuAnchor)}
          onClose={handleSortMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem
            onClick={() => handleSortSelect('nom-asc')}
            selected={sortType === 'nom-asc'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {sortType === 'nom-asc' && (
                <CheckIcon sx={{ mr: 1, fontSize: '18px' }} />
              )}
              {sortType !== 'nom-asc' && <Box sx={{ width: '26px' }} />}
              {t('bibliotequesPage.sort.nameAsc')}
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortSelect('nom-desc')}
            selected={sortType === 'nom-desc'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {sortType === 'nom-desc' && (
                <CheckIcon sx={{ mr: 1, fontSize: '18px' }} />
              )}
              {sortType !== 'nom-desc' && <Box sx={{ width: '26px' }} />}
              {t('bibliotequesPage.sort.nameDesc')}
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortSelect('livres-desc')}
            selected={sortType === 'livres-desc'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {sortType === 'livres-desc' && (
                <CheckIcon sx={{ mr: 1, fontSize: '18px' }} />
              )}
              {sortType !== 'livres-desc' && <Box sx={{ width: '26px' }} />}
              {t('bibliotequesPage.sort.booksDesc')}
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortSelect('livres-asc')}
            selected={sortType === 'livres-asc'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {sortType === 'livres-asc' && (
                <CheckIcon sx={{ mr: 1, fontSize: '18px' }} />
              )}
              {sortType !== 'livres-asc' && <Box sx={{ width: '26px' }} />}
              {t('bibliotequesPage.sort.booksAsc')}
            </Box>
          </MenuItem>
        </Menu>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          data-testid="libraries-add"
          onClick={handleAdd}
          sx={{
            height: '52px',
            borderRadius: 2,
            fontSize: { xs: '14px', sm: '14px' },
            whiteSpace: 'nowrap',
            px: { xs: 1, sm: 1.5 },
            flexShrink: 0,
            '& .MuiButton-startIcon': {
              mr: { xs: 0, sm: 0.75 }
            }
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {t('bibliotequesPage.add')}
          </Box>
        </Button>
      </Box>

      {/* Liste des bibliothèques */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {data?.length === 0 ? (
            <Alert severity="info">
              {search ? t('bibliotequesPage.noResults') : t('bibliotequesPage.empty')}
            </Alert>
          ) : (
            <Card sx={{ borderRadius: 2 }}>
              <Box sx={{ p: 3, pb: 2 }}>
                <Typography 
                  variant="h6" 
                  component="h2"
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '18px',
                    mb: 0.5,
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                  }}
                  >
                    {t('bibliotequesPage.title')}
                  </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                  }}
                >
                  {data?.length === 1 
                    ? t('bibliotequesPage.count', { count: data?.length || 0 })
                    : t('bibliotequesPage.countPlural', { count: data?.length || 0 })}
                </Typography>
              </Box>
              <Divider />
              {data?.map((bibliotheque) => (
                <BibliothequeCard
                  key={bibliotheque.id}
                  bibliotheque={bibliotheque}
                  rayonnages={rayonnagesData?.[bibliotheque.id] || []}
                />
              ))}
            </Card>
          )}
        </>
      )}

    </Box>
  );
}

export default BibliotequesPage;
